import { BoardQuery } from '@api/Trinium/types';
import { defaultDispatchColumns, defaultPinnedColumns } from '@components/DataGrid/Dispatch/columns';

/**
 * The selection checkbox, row actions, and order number are pinned on every board no matter what the query says.
 * Every other pin is the query's own, so that unpinning a column in the Board Query editor sticks.
 */
export const forcedPinnedFields = defaultDispatchColumns.map((column) => column.field);

export const defaultAllOpenQuery: BoardQuery = {
  id: 0,
  name: 'ALL UNDELIVERED',
  configuration: {
    columns: [
      'leg',
      'notes',
      'railBilling',
      'chassis',
      'driver',
      'pickup',
      'deliver',
      'container',
      'containerProvider',
      'puSealNo',
      'dehire',
      'customer',
      'scheduledPickup',
      'scheduledDelivery',
      'bookingBol',
      'releaseNo',
      'lastFreeDay',
      'vessel'
    ],
    pinnedCols: {
      left: [...(defaultPinnedColumns.left ?? []), 'Leg'],
      right: []
    },
    filters: [],
    sortOrder: []
  }
};

export const researchQuery: BoardQuery = {
  id: 1,
  name: 'RESEARCH',
  configuration: {
    columns: [
      'leg',
      'chassis',
      'driver',
      'pickup',
      'deliver',
      'container',
      'containerProvider',
      'customer',
      'scheduledPickup',
      'scheduledDelivery',
      'bookingBol',
      'deliverDate',
      'releaseNo',
      'csnNotes',
      'lastFreeDay'
    ],
    pinnedCols: {
      left: [...(defaultPinnedColumns.left ?? []), 'Leg']
    },
    filters: [],
    sortOrder: []
  }
};

/**
 * Configuration whose arrays are its own. The module-level query constants are handed out by reference all over the
 * app, so anything that could take ownership of one — the RTK cache, which Immer freezes, or an editor draft — has
 * to copy it first, or the constant is frozen or mutated app-wide.
 */
export function cloneQueryConfiguration(configuration: BoardQuery['configuration']): BoardQuery['configuration'] {
  const { columns, pinnedCols, filters, sortOrder } = configuration;

  return {
    ...configuration,
    columns: [...columns],
    pinnedCols: { left: [...(pinnedCols.left ?? [])], right: [...(pinnedCols.right ?? [])] },
    filters: [...filters],
    sortOrder: sortOrder ? [...sortOrder] : undefined
  };
}

/**
 * Strips the pins the grid injects on read back out, so the stored query carries only its own. Inverse of
 * withForcedPins — every write body goes through this, whether it is a create or an update.
 */
export function toBoardQueryBody(query: Pick<BoardQuery, 'name' | 'configuration' | 'isActive'>) {
  return {
    name: query.name,
    isActive: query.isActive,
    configuration: {
      ...query.configuration,
      pinnedCols: {
        ...query.configuration.pinnedCols,
        left: (query.configuration.pinnedCols.left ?? []).filter((field) => !forcedPinnedFields.includes(field))
      }
    }
  };
}

/** Server queries always carry the flag once transformed; only the client-side ALL UNDELIVERED goes without it, and it is always live. */
export function isActiveQuery(query: BoardQuery) {
  return query.isActive !== false;
}

/**
 * Adds the pinned columns the grid always needs, which the API neither stores nor returns. Every query entering the
 * fetchBoard cache goes through this, whether it arrived from a fetch or from a create.
 */
export function withForcedPins(query: BoardQuery): BoardQuery {
  return {
    ...query,
    configuration: {
      ...query.configuration,
      pinnedCols: {
        left: [...new Set([...forcedPinnedFields, ...(query.configuration.pinnedCols.left ?? [])])],
        right: [...(query.configuration.pinnedCols.right ?? [])]
      }
    }
  };
}

/**
 * Transform the Board Query response.
 * Add the default query to the response collection.
 * Map each returned query to add the pinned columns the grid always needs.
 */
export function transformBoardQueryResponse(result: BoardQuery[]) {
  // The default query goes in as a copy: the cache is Immer-managed, and the constant itself is shared app-wide
  const allOpen: BoardQuery = { ...defaultAllOpenQuery, configuration: cloneQueryConfiguration(defaultAllOpenQuery.configuration) };

  // The API omits fields left at their initial value, and isActive starts out false, so a disabled query arrives without it
  return [allOpen, ...result.map((query) => withForcedPins({ ...query, isActive: query.isActive ?? false }))];
}
